/**
 * Undo/Redo Manager using Yjs UndoManager
 *
 * Tracks local changes to collaborative state and allows users to
 * reverse their own edits without affecting other users' changes.
 *
 * Only local Yjs mutations that should participate in global undo/redo
 * should use LOCAL_ORIGIN as their transaction origin.
 */

import * as Y from 'yjs'
import { LOCAL_ORIGIN } from './constants'

const ANNOTATION_CONTENT_PREFIX = 'annotation-content-'
const DEFAULT_CAPTURE_TIMEOUT = 500
const EXTENDED_CAPTURE_TIMEOUT = 60_000

let undoManager: Y.UndoManager | null = null
let undoManagerDoc: Y.Doc | null = null
let ySyncTrackingRefCount = 0
/**
 * The transaction origin y-prosemirror stamps on editor writes
 * (`ySyncPluginKey` from `@tiptap/y-tiptap`). Registered by the first text
 * editor that enables tracking rather than imported here: this module sits in
 * the editor's first-paint bundle through the store, and importing the key
 * statically dragged the whole ProseMirror/tiptap graph in with it. The key is
 * a PluginKey instance, so identity matters — callers must pass the real one.
 */
let ySyncPluginKey: unknown = null
let undoRefocusSuppressed = false
/** >0 while runAsSingleUndoStep is on the call stack. */
let singleUndoStepDepth = 0
/** Monotonic counter used to cancel stale deferred tracking restores. */
let syncTrackingRestoreEpoch = 0
let observedTextAnnotations: Y.Map<unknown> | null = null
let textAnnotationsObserver: ((event: Y.YMapEvent<unknown>) => void) | null = null

function getAnnotationContentFragment(ydoc: Y.Doc, annotationId: string): Y.XmlFragment {
  return ydoc.getXmlFragment(`${ANNOTATION_CONTENT_PREFIX}${annotationId}`)
}

function syncTextAnnotationScope(ydoc: Y.Doc): void {
  if (!undoManager) return

  const textAnnotations = ydoc.getMap('textAnnotations')
  for (const key of textAnnotations.keys()) {
    if (typeof key === 'string') {
      undoManager.addToScope(getAnnotationContentFragment(ydoc, key))
    }
  }
}

function detachTextAnnotationScopeObserver(): void {
  if (observedTextAnnotations && textAnnotationsObserver) {
    observedTextAnnotations.unobserve(textAnnotationsObserver)
  }
  observedTextAnnotations = null
  textAnnotationsObserver = null
}

function attachTextAnnotationScopeObserver(ydoc: Y.Doc): void {
  detachTextAnnotationScopeObserver()

  const textAnnotations = ydoc.getMap('textAnnotations')
  const observer = (event: Y.YMapEvent<unknown>) => {
    if (!undoManager) return

    for (const [key, change] of event.changes.keys) {
      if (typeof key !== 'string') continue

      if (change.action !== 'delete') {
        const fragment = getAnnotationContentFragment(ydoc, key)
        undoManager.addToScope(fragment)
      }
      // Never remove fragments from scope on deletion. The scope observer
      // fires AFTER the Yjs transaction commits, but popStackItem processes
      // redo items DURING the transaction. If we remove the fragment here
      // (during undo of annotation creation), the subsequent redo of that
      // creation can't find the fragment in scope — so the paragraph item
      // is skipped. Then the text redo fails because the paragraph parent
      // is still deleted (redoItem returns null), and Yjs silently consumes
      // the text redo item. Keeping orphaned fragments in scope is harmless.
    }
  }

  textAnnotations.observe(observer)
  observedTextAnnotations = textAnnotations
  textAnnotationsObserver = observer

  syncTextAnnotationScope(ydoc)
}

/**
 * Initialize the UndoManager for a project's Y.Doc
 *
 * Scope includes all undoable maps:
 * - clips, tracks, sequences, sequenceFrames, sequenceFolders
 * - canvasPositions, scenes, takeCustomizations, assetInOutPoints
 * - assets (only setAssetRating/setAssetColorLabel/removeNodes use LOCAL_ORIGIN)
 * - canvasBins (for bin creation/deletion undo)
 * - variationStacks, subjectCollections (for grouping undo)
 * - textAnnotations, characterTransforms
 * - jobs, draftSettings (only removeNodes uses LOCAL_ORIGIN)
 * - elementCanvasNodes (add/update/remove subject canvas nodes)
 *
 * NOT included:
 * - meta (not used for undoable operations)
 *
 * Note: yAssets is in scope — rating/colorLabel and removeNodes (batch canvas delete)
 * use LOCAL_ORIGIN. addAsset/updateAsset/removeAsset remain non-undoable.
 */
export function initUndoManager(ydoc: Y.Doc): Y.UndoManager {
  if (undoManager && undoManagerDoc === ydoc) {
    return undoManager
  }
  if (undoManager) {
    destroyUndoManager()
  }

  const scope = [
    ydoc.getMap('clips'),
    ydoc.getMap('tracks'),
    ydoc.getMap('sequences'),
    ydoc.getMap('sequenceFrames'),
    ydoc.getMap('sequenceFolders'),
    ydoc.getMap('canvasPositions'),
    ydoc.getMap('scenes'),
    ydoc.getMap('takeCustomizations'),
    ydoc.getMap('assetInOutPoints'),
    ydoc.getMap('assets'),
    ydoc.getMap('jobs'),
    ydoc.getMap('canvasBins'),
    ydoc.getMap('variationStacks'),
    ydoc.getMap('subjectCollections'),
    ydoc.getMap('textAnnotations'),
    ydoc.getMap('characterTransforms'),
    ydoc.getMap('draftSettings'),
    ydoc.getMap('elementCanvasNodes'),
    ydoc.getMap('variableCanvasNodes'),
    ydoc.getMap('setSubjectPlacements'),
    // Action cards delete alongside their canvasPositions entry; without the
    // map in scope, Cmd+Z would restore an orphan position for a gone record.
    ydoc.getMap('actions'),
    // Naming a group writes the record AND stamps its members in one
    // transaction; with only `actions` in scope, Cmd+Z stripped the stamps
    // and left an orphan record (or restored stamps to a record it could not
    // bring back, so the group silently lost its name).
    ydoc.getMap('workflows'),
  ]

  undoManager = new Y.UndoManager(scope, {
    trackedOrigins: new Set([LOCAL_ORIGIN]),
    captureTimeout: DEFAULT_CAPTURE_TIMEOUT,
  })
  undoManagerDoc = ydoc

  if (ySyncTrackingRefCount > 0 && ySyncPluginKey !== null) {
    undoManager.addTrackedOrigin(ySyncPluginKey)
  }

  attachTextAnnotationScopeObserver(ydoc)

  return undoManager
}

/**
 * Get the current UndoManager instance
 */
export function getUndoManager(): Y.UndoManager | null {
  return undoManager
}

/**
 * Destroy the UndoManager (call on project switch/cleanup)
 */
export function destroyUndoManager(): void {
  detachTextAnnotationScopeObserver()

  if (undoManager) {
    undoManager.destroy()
    undoManager = null
    undoManagerDoc = null
    ySyncTrackingRefCount = 0
    undoRefocusSuppressed = false
    syncTrackingRestoreEpoch++
  }
}

/**
 * Check if undo is available
 */
export function canUndo(): boolean {
  return undoManager?.undoStack.length ? undoManager.undoStack.length > 0 : false
}

/**
 * Check if redo is available
 */
export function canRedo(): boolean {
  return undoManager?.redoStack.length ? undoManager.redoStack.length > 0 : false
}

/**
 * Perform undo.
 *
 * Temporarily removes ySyncPluginKey from tracked origins so that any
 * y-prosemirror sync-plugin write-backs that occur during the undo
 * transaction are NOT captured as new undo items (which would clear the
 * redo stack). After the undo completes, tracking is restored unless
 * the refocus flow took over management (undoRefocusSuppressed === true).
 */
export function undo(): void {
  withSuppressedSyncTracking(() => undoManager?.undo())
}

/**
 * Perform redo (same sync-tracking guard as undo).
 */
export function redo(): void {
  withSuppressedSyncTracking(() => undoManager?.redo())
}

function withSuppressedSyncTracking(fn: () => void): void {
  if (!undoManager) return
  const wasTracked = ySyncPluginKey !== null && undoManager.trackedOrigins.has(ySyncPluginKey)
  if (wasTracked) {
    undoManager.removeTrackedOrigin(ySyncPluginKey)
  }
  try {
    fn()
  } finally {
    // If the refocus flow took over (text annotation affected + editor mounted),
    // it will re-add tracking via resumeTextUndoTrackingAfterRefocus(). Done.
    if (undoRefocusSuppressed) return

    if (wasTracked && ySyncTrackingRefCount > 0) {
      // Defer the restore by 3 rAFs instead of restoring immediately.
      // This gives newly created text editors time to mount and run their
      // sync-plugin initialisation (which writes to YDoc with ySyncPluginKey).
      // Without the deferral those writes are captured as phantom undo items,
      // clearing the redo stack.
      const epoch = ++syncTrackingRestoreEpoch
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (epoch === syncTrackingRestoreEpoch && ySyncTrackingRefCount > 0 && undoManager) {
              undoManager.addTrackedOrigin(ySyncPluginKey)
            }
          })
        })
      })
    }
  }
}

/**
 * Stop capturing - call after discrete operations to prevent merging
 *
 * Use after operations like:
 * - commitDrag()
 * - splitClip()
 * - rippleDeleteClips()
 * - atomicDeleteTrack()
 * - removeSequence()
 * - duplicateSequence()
 */
export function stopCapturing(): void {
  if (singleUndoStepDepth > 0) return
  undoManager?.stopCapturing()
}

/**
 * Run a synchronous, multi-mutation gesture as ONE undo step.
 *
 * Store mutations end with stopCapturing() so each is a discrete step on its
 * own — right for a single call, wrong for a gesture that composes several
 * (a bulk canvas delete removes notes, shots, bins, stacks and action cards
 * through separate store functions, which used to cost one ⌘Z per kind).
 * Inside `fn` those stopCapturing() calls are held back and the capture window
 * is widened so a slow transaction can't split the group; the step is closed
 * on the way out, error or not.
 */
export function runAsSingleUndoStep<T>(fn: () => T): T {
  if (singleUndoStepDepth === 0 && undoManager) {
    undoManager.stopCapturing()
    undoManager.captureTimeout = EXTENDED_CAPTURE_TIMEOUT
  }
  singleUndoStepDepth++
  try {
    return fn()
  } finally {
    singleUndoStepDepth--
    if (singleUndoStepDepth === 0 && undoManager) {
      undoManager.captureTimeout = DEFAULT_CAPTURE_TIMEOUT
      undoManager.stopCapturing()
    }
  }
}

/**
 * Ref-counted tracking of ySyncPluginKey on the global UndoManager.
 *
 * ySyncPluginKey is the origin y-prosemirror uses when syncing ProseMirror
 * edits to Y.Doc. We only track it while a text editor is actively being
 * edited to avoid phantom captures (e.g. editor initialization writing a
 * default paragraph to an empty Y.XmlFragment) that would clear the redo stack.
 */
export function enableTextUndoTracking(syncOrigin: unknown): void {
  ySyncPluginKey = syncOrigin
  ySyncTrackingRefCount++
  if (ySyncTrackingRefCount === 1 && !undoRefocusSuppressed) {
    undoManager?.addTrackedOrigin(ySyncPluginKey)
  }
}

/**
 * Suppress ySyncPluginKey tracking during undo/redo-triggered refocus.
 *
 * When an undo/redo re-enters text edit mode, the editor sync plugin emits
 * mutations as it re-syncs with the Y.XmlFragment. If those mutations are
 * tracked they appear as new undo items — clearing the redo stack and
 * creating phantom history entries. Call this before the refocus, then call
 * resumeTextUndoTrackingAfterRefocus() once the editor sync settles.
 */
export function suppressTextUndoTrackingDuringRefocus(): void {
  undoRefocusSuppressed = true
  // Cancel any pending deferred restore from withSuppressedSyncTracking —
  // the refocus flow will manage re-addition via resume.
  syncTrackingRestoreEpoch++
  // Also remove ySyncPluginKey if it was already tracked (i.e. the user was
  // editing when undo/redo fired). Without this, deferred sync-plugin writes
  // during the refocus window are captured as new undo items, clearing the
  // redo stack.
  if (ySyncPluginKey !== null) undoManager?.removeTrackedOrigin(ySyncPluginKey)
}

export function resumeTextUndoTrackingAfterRefocus(): void {
  undoRefocusSuppressed = false
  if (ySyncTrackingRefCount > 0 && undoManager && ySyncPluginKey !== null) {
    undoManager.addTrackedOrigin(ySyncPluginKey)
  }
}

export function disableTextUndoTracking(): void {
  ySyncTrackingRefCount = Math.max(0, ySyncTrackingRefCount - 1)
  if (ySyncTrackingRefCount === 0 && ySyncPluginKey !== null) {
    undoManager?.removeTrackedOrigin(ySyncPluginKey)
  }
}

/**
 * Temporarily extend the capture timeout for long-running continuous operations
 * (e.g., 3D gizmo drags where frame rates can be variable).
 * Call endExtendedCapture() when done to restore the default timeout.
 */
export function beginExtendedCapture(): void {
  if (undoManager) {
    undoManager.captureTimeout = EXTENDED_CAPTURE_TIMEOUT
  }
}

/**
 * Restore the default capture timeout and stop capturing.
 * Ensures the completed operation becomes one discrete undo step.
 */
export function endExtendedCapture(): void {
  if (undoManager) {
    undoManager.captureTimeout = DEFAULT_CAPTURE_TIMEOUT
    undoManager.stopCapturing()
  }
}
